// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";
import { Button } from "react-bootstrap";

import { TimePeriod, DashboardPanel } from "types";
import { IAction, ActionType, raiseAction, IActionTypeSelectGuiItem } from "actions";
import {
  getGraphBoundaryDates,
  getTrendIoGraphViewMode,
  getDateString,
  getPanelSelection,
  getIoType,
} from "guiutils";
import { IState } from "states/states";
import { IPeriodData, IStateGui } from "states/guistates";
import { GraphViewMode } from "states/cachedstates";

import { ReactComponent as SvgBarView } from "static/iconBarView.svg";
import { ReactComponent as SvgLineView } from "static/iconGraphView.svg";
import { ReactComponent as SvgHorizontalView } from "static/iconHorizontalBarView.svg";
import { ReactComponent as SvgListView } from "static/iconListView.svg";

interface ITrendHeadingExtProps {
  identifier: keyof IStateGui;
  showGraphModes?: GraphViewMode[];
  showTrendData?: boolean;
  includeDate: boolean;
  title?: string;
  panel?: DashboardPanel;
  dashboardIndex?: number;
}

interface ITrendHeadingProps {
  trendMember: "energy" | "power" | "costs";
  xZoom: TimePeriod;
  minX?: number;
  maxX?: number;
  graphStart: number | undefined;
  graphEnd: number | undefined;
  isAtEnd: boolean;
  graphViewMode: GraphViewMode;
  selectedDashboardIndex: number;
}

const mapStateToProps = (state: IState, ownProps: ITrendHeadingExtProps) => {
  const { index, selection } = getPanelSelection(
    state,
    ownProps.panel ?? DashboardPanel.ENERGY,
    ownProps.dashboardIndex,
  );
  const curPeriod = state.gui[ownProps.identifier] as IPeriodData;
  const graphDates = getGraphBoundaryDates(
    curPeriod.startDate,
    curPeriod.endDate,
    state.site.tzOffset,
    curPeriod.timePeriod,
  );

  const props: ITrendHeadingProps = {
    trendMember: state.gui.trendMember,
    xZoom: curPeriod.timePeriod,
    minX: curPeriod.zoomMin,
    maxX: curPeriod.zoomMax,
    graphStart: graphDates.startDate,
    graphEnd: graphDates.endDate,
    isAtEnd: curPeriod.startDate === undefined,
    selectedDashboardIndex: index,
    graphViewMode:
      getTrendIoGraphViewMode(
        selection,
        getIoType(state),
        Object.keys(state.trending.ioTrendFull).length > 0,
        ownProps.showTrendData ?? false,
      ) ?? GraphViewMode.BAR,
  };
  return props;
};

interface ITrendHeadingDispatch {
  onSwitchGraphView: (dashboardIndex: number, selection: GraphViewMode) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: ITrendHeadingDispatch = {
    onSwitchGraphView: (dashboardIndex: number, selection: GraphViewMode) => {
      const payload: IActionTypeSelectGuiItem = { dashboardIndex, selection };
      dispatch(raiseAction(ActionType.ACT_SELECT_GRAPHVIEWMODE, payload));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  switchToGraph: {
    id: "switch to graph view",
  },
  switchToList: {
    id: "switch to list view",
  },
});

class TrendHeadingComponent extends React.Component<
  ITrendHeadingProps & ITrendHeadingExtProps & ITrendHeadingDispatch & WrappedComponentProps
> {
  public render() {
    const dateString =
      getDateString(
        this.props.xZoom,
        this.props.graphStart,
        this.props.graphEnd,
        this.props.isAtEnd,
        this.props.intl.formatMessage({
          id: `thisperiod_${this.props.xZoom ?? ""}`,
        }),
        this.props.intl.locale,
      ) ?? "-";

    return (
      <div id="trendheading">
        <h1>
          {this.props.title ?? <FormattedMessage id={`TrendMember_${this.props.trendMember}`} />}
          {this.props.includeDate && dateString && (
            <>
              : <span className="trendheadingdate">{dateString}</span>
            </>
          )}
        </h1>
        {this.props.showGraphModes?.length && (
          <div id="graphswitch">
            {this.props.showGraphModes.includes(GraphViewMode.BAR) === true && (
              <Button
                variant="secondary"
                className={this.props.graphViewMode === GraphViewMode.BAR ? "active" : undefined}
                title={this.props.intl.formatMessage(messages.switchToGraph)}
                onClick={this.props.onSwitchGraphView.bind(
                  this,
                  this.props.selectedDashboardIndex,
                  GraphViewMode.BAR,
                )}
              >
                <SvgBarView />
              </Button>
            )}
            {this.props.showGraphModes.includes(GraphViewMode.LINE) === true && (
              <>
                <Button
                  variant="secondary"
                  className={this.props.graphViewMode === GraphViewMode.LINE ? "active" : undefined}
                  title={this.props.intl.formatMessage(messages.switchToGraph)}
                  onClick={this.props.onSwitchGraphView.bind(
                    this,
                    this.props.selectedDashboardIndex,
                    GraphViewMode.LINE,
                  )}
                >
                  <SvgLineView />
                </Button>
              </>
            )}
            {this.props.showGraphModes.includes(GraphViewMode.DIGITAL) === true && (
              <>
                <Button
                  variant="secondary"
                  className={
                    this.props.graphViewMode === GraphViewMode.DIGITAL ? "active" : undefined
                  }
                  title={this.props.intl.formatMessage(messages.switchToGraph)}
                  onClick={this.props.onSwitchGraphView.bind(
                    this,
                    this.props.selectedDashboardIndex,
                    GraphViewMode.DIGITAL,
                  )}
                >
                  <SvgHorizontalView />
                </Button>
              </>
            )}
            {this.props.showGraphModes.includes(GraphViewMode.TABLE) === true && (
              <Button
                variant="secondary"
                className={this.props.graphViewMode === GraphViewMode.TABLE ? "active" : undefined}
                title={this.props.intl.formatMessage(messages.switchToList)}
                onClick={this.props.onSwitchGraphView.bind(
                  this,
                  this.props.selectedDashboardIndex,
                  GraphViewMode.TABLE,
                )}
              >
                <SvgListView />
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(TrendHeadingComponent));
